/** Shared recon “seen” id — panel, sidebar, browser notify. */
export const RECON_SEEN_KEY = 'jgi.reconSeenId.v1';
const LEGACY_SEEN_KEYS = [
  'jgi.fxReconLastSeenId.v1',
  'jgi.reconNotifySeenId.v1',
  'jgi.reconSidebarSeen.v1',
];

const NOTIFY_KEY = 'jgi.reconNotify.v1';
const POLL_MS = 45_000;

const MATCH_ACTIONS = new Set([
  'match',
  'partial',
  'group',
  'batch-match',
  'batch_match',
  'cleared',
  'release',
]);

type ReconEventLite = {
  id: number;
  action: string;
  note?: string | null;
  actor?: string | null;
  createdAt: string;
  companyId?: number | null;
  payload?: Record<string, unknown> | null;
};

export function migrateReconSeenId(): number {
  if (!import.meta.client) return 0;
  const existing = Number(localStorage.getItem(RECON_SEEN_KEY) || 0);
  if (existing > 0) return existing;
  let max = 0;
  for (const k of LEGACY_SEEN_KEYS) {
    max = Math.max(max, Number(localStorage.getItem(k) || 0));
  }
  if (max > 0) localStorage.setItem(RECON_SEEN_KEY, String(max));
  return max;
}

export function writeReconSeenId(id: number) {
  if (!import.meta.client || !(id > 0)) return;
  localStorage.setItem(RECON_SEEN_KEY, String(id));
  for (const k of LEGACY_SEEN_KEYS) {
    try {
      localStorage.setItem(k, String(id));
    } catch {
      /* ignore */
    }
  }
}

export function useReconNotifications() {
  const enabled = ref(false);
  const permission = ref<NotificationPermission>('default');
  const lastEventId = ref(0);

  function syncPermission() {
    if (!import.meta.client || typeof Notification === 'undefined') {
      permission.value = 'denied';
      return;
    }
    permission.value = Notification.permission;
  }

  function loadPrefs() {
    if (!import.meta.client) return;
    enabled.value = localStorage.getItem(NOTIFY_KEY) === '1';
    lastEventId.value = migrateReconSeenId();
    syncPermission();
  }

  async function enable() {
    if (!import.meta.client || typeof Notification === 'undefined') return false;
    const p = await Notification.requestPermission();
    permission.value = p;
    if (p === 'granted') {
      enabled.value = true;
      localStorage.setItem(NOTIFY_KEY, '1');
      return true;
    }
    enabled.value = false;
    localStorage.setItem(NOTIFY_KEY, '0');
    return false;
  }

  function disable() {
    enabled.value = false;
    if (import.meta.client) localStorage.setItem(NOTIFY_KEY, '0');
  }

  async function toggle() {
    if (enabled.value) {
      disable();
      return false;
    }
    return enable();
  }

  function notifyMatch(ev: ReconEventLite) {
    if (!import.meta.client || typeof Notification === 'undefined') return;
    if (permission.value !== 'granted' || !enabled.value) return;
    if (!MATCH_ACTIONS.has(String(ev.action || '').toLowerCase())) return;

    const p = (ev.payload || {}) as Record<string, unknown>;
    const body =
      String(ev.note || '').slice(0, 120) ||
      (p.leftRef && p.rightRef ? `${p.leftRef} ↔ ${p.rightRef}` : 'Rekonsiliasi berhasil');

    const entryId = Number(p.leftEntryId || p.rightEntryId || p.entryId || 0);
    const q = new URLSearchParams();
    if (entryId > 0) q.set('entry', String(entryId));
    if (ev.companyId) q.set('companyId', String(ev.companyId));
    const href = q.toString() ? `/reconciliation?${q}` : '/reconciliation';

    try {
      const n = new Notification('Rekonsiliasi · Cocokkan berhasil', {
        body: `${body}\n${ev.actor || '—'} · ${new Date(ev.createdAt).toLocaleString('id-ID')}`,
        icon: '/favicon.ico',
        tag: `recon-${ev.id}`,
      });
      n.onclick = () => {
        window.focus();
        window.location.href = href;
        n.close();
      };
    } catch {
      /* ignore */
    }
  }

  async function pollNewMatches() {
    if (!import.meta.client || !enabled.value || permission.value !== 'granted') return;
    try {
      const rows = await $fetch<ReconEventLite[]>('/api/reconciliation/timeline', {
        query: { limit: 5 },
      });
      if (!Array.isArray(rows) || !rows.length) return;
      const top = rows[0]!;
      if (top.id <= lastEventId.value) return;

      const fresh = rows.filter((r) => r.id > lastEventId.value);
      for (const ev of fresh.reverse()) {
        if (MATCH_ACTIONS.has(String(ev.action || '').toLowerCase())) {
          notifyMatch(ev);
        }
      }
      lastEventId.value = top.id;
      writeReconSeenId(top.id);
    } catch {
      /* ignore */
    }
  }

  /** Hanya panggil saat user membuka panel / menganggap sudah dilihat. */
  function markSeenFromEvents(events: ReconEventLite[]) {
    const top = events[0];
    if (!top?.id || !import.meta.client) return;
    if (top.id > lastEventId.value) {
      lastEventId.value = top.id;
      writeReconSeenId(top.id);
    }
  }

  return {
    enabled,
    permission,
    lastEventId,
    loadPrefs,
    enable,
    disable,
    toggle,
    notifyMatch,
    pollNewMatches,
    markSeenFromEvents,
    POLL_MS,
    RECON_SEEN_KEY,
    migrateReconSeenId,
    writeReconSeenId,
  };
}
